import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useOrganizationWageSetMutationHandlers } from "../hooks/use-organization-wage-set-mutations";
import { getTodayInTimezone, isCalendarDate } from "../utils/wage-dates";
import {
  emptyWageFormValues,
  wageFormValuesToInput,
  validateWageForm,
  type WageFormErrors,
} from "../utils/wage-fields";
import { DateField } from "./date-field";
import { OrganizationWageSetDialog } from "./organization-wage-set-dialog";
import { WageFields } from "./wage-fields";

const NAME_MAX_LENGTH = 120;

type CreateOrganizationWageSetDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreateOrganizationWageSetDialog({
  open,
  onOpenChange,
}: CreateOrganizationWageSetDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { invalidateOrganizationWageSets, handleError } =
    useOrganizationWageSetMutationHandlers();
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState(() =>
    getTodayInTimezone({ timezoneId: selectedOrgBusinessUnit.timezoneId }),
  );
  const [wages, setWages] = useState(emptyWageFormValues);
  const [wageErrors, setWageErrors] = useState<WageFormErrors>({});
  const [nameError, setNameError] = useState<string>();
  const [dateError, setDateError] = useState<string>();

  const createMutation = useMutation(
    trpc.organizationWageSet.create.mutationOptions({
      onSuccess: async () => {
        await invalidateOrganizationWageSets();
        toast.success("Wage set created");
        onOpenChange(false);
      },
      onError: handleError,
    }),
  );

  function handleSubmit() {
    const trimmedName = name.trim();
    const nextWageErrors = validateWageForm(wages);
    const nextNameError = trimmedName ? undefined : "Enter a name";
    const nextDateError = isCalendarDate(startsOn)
      ? undefined
      : "Choose a start date";
    setWageErrors(nextWageErrors);
    setNameError(nextNameError);
    setDateError(nextDateError);
    if (
      nextNameError ||
      nextDateError ||
      Object.keys(nextWageErrors).length > 0
    ) {
      return;
    }
    createMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      name: trimmedName,
      startsOn,
      wages: wageFormValuesToInput(wages),
    });
  }

  return (
    <OrganizationWageSetDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create wage set"
      description="Name the pay terms and set the first wages. You can change wages later without losing history."
      submitLabel="Create wage set"
      isPending={createMutation.isPending}
      onSubmit={handleSubmit}
    >
      <div className="space-y-1.5">
        <Label htmlFor="organization-wage-set-name">Name</Label>
        <Input
          id="organization-wage-set-name"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          placeholder="Clinic visits"
          onChange={(event) => setName(event.target.value)}
          aria-invalid={Boolean(nameError)}
        />
        {nameError && <p className="text-sm text-red-600">{nameError}</p>}
      </div>
      <DateField
        label="Starts on"
        value={startsOn}
        onChange={setStartsOn}
        error={dateError}
        hint="May be in the past."
      />
      <WageFields values={wages} errors={wageErrors} onChange={setWages} />
    </OrganizationWageSetDialog>
  );
}
